/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import Slider, { Settings as SlickSettings } from "react-slick";
import styled from "styled-components";
import {MEDIA_URL} from "app/constants/APIKEYS";
import {Text} from "styles/reactnative";
import {AutoPlayVideo} from "../../HomeTown/Common/AutoPlayVideo";
import { clampSaleCardScale, resolveTransactionColor, useResponsiveScale } from "./DigitalSignage/SaleInfoCard";


/* ---------- 타입 ---------- */
type FitMode = "cover" | "contain";

type FullImageListProps = {
    imagelist: string[];
    /** 이미지/영상 핏(기본 cover) */
    fit?: FitMode;
    /** 레이아웃 타입 */
    type?: "full" | "two" | "six";
    /** 부모 슬라이드 아이템 원본(체류시간/텍스트 사용) */
    setItem?: any;
    isWidth?: boolean;
    width?: string;
    height?: string;
    /** 현재 카드 인덱스 */
    num?: number;
    /** 부모 인터벌 시간 동기화 */
    setIntervalTime?: (ms: number) => void;
    /** 전체 뷰포트 기반 스케일 */
    globalScale?: number;
    /** 현재 슬라이드 활성 여부 */
    isActive?: boolean;
    /** 활성 상태가 되기 전까지 이미지 렌더를 지연 */
    deferUntilActive?: boolean;
    /** 강제 렌더링 플래그(미리보기 등) */
    forceLoad?: boolean;
};

/* ---------- 유틸 ---------- */
const joinUrl = (base: string, path: string) =>
    `${base?.replace(/\/+$/, "")}/${(path || "").replace(/^\/+/, "")}`;

// mp4/webm/mov/HLS(m3u8)/DASH(mpd) 까지 커버
const isVideoUrl = (rawUrl: string) =>
    /\.(mp4|webm|mov|m3u8|mpd)(\?.*)?$/i.test(rawUrl || "");
const isGifUrl = (rawUrl: string) => /\.gif(\?.*)?$/i.test(rawUrl || "");

const encodeFilenameOnly = (rawPath: string) => {
    if (!rawPath) return "";
    const [pathNoQuery, query = ""] = rawPath.split("?");
    const parts = pathNoQuery.split("/");
    const filename = parts.pop() || "";
    const safe = [...parts, encodeURI(filename)].join("/");
    return query ? `${safe}?${query}` : safe;
};

const FULL_LAYOUT_SLIDE_INTERVAL_MS = 5000;
const FULL_LAYOUT_MAX_IMAGES = 6;
const fullLayoutSlickSettings: SlickSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: FULL_LAYOUT_SLIDE_INTERVAL_MS,
    pauseOnHover: false,
    swipe: false,
    draggable: false,
    fade: true,
    speed: 500,
};

/* ---------- 컴포넌트 ---------- */
const FullImageListComponent: React.FC<FullImageListProps> = ({
                                                                imagelist,
                                                                fit = "cover",
                                                                type = "full",
                                                                setItem,
                                                                num = 0,
                                                                setIntervalTime,
                                                                globalScale = 1,
                                                                isActive = false,
                                                                deferUntilActive = false,
                                                                forceLoad = false,
                                                            }) => {
    const [current, setCurrent] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const fullTextRef = useRef<HTMLDivElement | null>(null);
    const baseScale = useResponsiveScale(fullTextRef, 620, { min: 0.55, max: 1.1 });
    const textScale = useMemo(
        () => clampSaleCardScale(baseScale * globalScale),
        [baseScale, globalScale]
    );

    const badSrcRef = useRef<Set<string>>(new Set());
    const lastAdvanceAtRef = useRef<number>(0);
    const resolvedMediaList = useMemo(() => {
        if (!imagelist || imagelist.length === 0) return [];
        const baseList = imagelist
            .slice(0, FULL_LAYOUT_MAX_IMAGES)
            .map((raw) => {
                const safe = encodeFilenameOnly(raw);
                const full = safe ? joinUrl(MEDIA_URL, safe) : "";
                return {
                    safe,
                    full,
                    isVideo: isVideoUrl(safe),
                    isAnimatedGif: isGifUrl(safe),
                };
            })
            .filter((media) => media.full.length > 0);
        return baseList
            .map((media) => ({
                ...media,
                isAnimatedGif: isGifUrl(media.safe || ''),
            }))
            .sort((a, b) => {
            const nameA = a.safe?.split('/').pop() ?? '';
            const nameB = b.safe?.split('/').pop() ?? '';
            return nameA.localeCompare(nameB);
        });
    }, [imagelist]);
    const currentMedia = resolvedMediaList[current] ?? resolvedMediaList[0] ?? null;
    const fullUrl = currentMedia?.full ?? "";
    const isVideo = currentMedia?.isVideo ?? false;

    /* 부모 인터벌 싱크(옵션) */
    useEffect(() => {
        if (!isActive || !setIntervalTime || !setItem) return;
        const sec = Number(setItem?.[num]?.sec);
        if (sec && !Number.isNaN(sec)) setIntervalTime(sec * 1000);
    }, [setItem, num, setIntervalTime, isActive]);

    const saleInfo = setItem?.[num]?.saleInfo || setItem?.[0]?.saleInfo || {};
    const hasSaleType = !!saleInfo?.saleType;
    const hasVideoMedia = resolvedMediaList.some((media) => media.isVideo);
    const containsGif = useMemo(
        () => resolvedMediaList.some((media) => media.isAnimatedGif),
        [resolvedMediaList],
    );
    const shouldUseImageSlider =
        resolvedMediaList.length > 1 &&
        !hasVideoMedia &&
        !containsGif &&
        (type === "full" || type === "two");
    const shouldRenderNow = forceLoad || !deferUntilActive || isActive;
    const activeMedia = resolvedMediaList[current] ?? resolvedMediaList[0] ?? null;
    const [gifResetKey, setGifResetKey] = useState(0);

    useEffect(() => {
        setCurrent(0);
    }, [resolvedMediaList.length, type, num]);

    const prevActiveRef = useRef(false);
    useEffect(() => {
        if (!prevActiveRef.current && isActive) {
            setCurrent(0);
            if (containsGif) {
                setGifResetKey((prev) => prev + 1);
            }
        }
        prevActiveRef.current = isActive;
    }, [isActive, containsGif]);

    useEffect(() => {
        if (containsGif) {
            setGifResetKey((prev) => prev + 1);
        }
    }, [type, num, containsGif]);

    const withGifReset = useCallback(
        (url: string): string => {
            if (!containsGif || !gifResetKey || !url) return url;
            return url.includes('?')
                ? `${url}&_gifReset=${gifResetKey}`
                : `${url}?_gifReset=${gifResetKey}`;
        },
        [containsGif, gifResetKey],
    );

    const advanceToNext = useCallback(() => {
        const now = Date.now();
        if (now - lastAdvanceAtRef.current < 80) return;
        lastAdvanceAtRef.current = now;

        setCurrent((prev) => {
            if (resolvedMediaList.length === 0) return 0;
            return prev === resolvedMediaList.length - 1 ? 0 : prev + 1;
        });
    }, [resolvedMediaList.length]);

    /* 카드 내부 이미지/영상 순환 타이머 */
    useEffect(() => {
        if (!isActive || shouldUseImageSlider || resolvedMediaList.length <= 1) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        let intervalMs = FULL_LAYOUT_SLIDE_INTERVAL_MS;
        if (!(type === "full" && hasSaleType)) {
            const sec = Number(setItem?.[num]?.sec);
            intervalMs = sec && !Number.isNaN(sec) ? sec * 1000 : FULL_LAYOUT_SLIDE_INTERVAL_MS;
        }

        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(advanceToNext, intervalMs);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [resolvedMediaList, setItem, num, type, hasSaleType, shouldUseImageSlider, advanceToNext, isActive]);

    const sliderSettings = useMemo(() => {
        return {
            ...fullLayoutSlickSettings,
            autoplay: isActive,
        };
    }, [isActive]);

    /* --------- 레이아웃 분기 --------- */
    const isBad = fullUrl && badSrcRef.current.has(fullUrl);

    // 공통 폴백
    if (!imagelist || imagelist.length === 0) {
        return (
            <FallbackBox>
                <FallbackText>No Images.</FallbackText>
            </FallbackBox>
        );
    }

    const renderMedia = (keySuffix: string) => {
        if (shouldUseImageSlider) {
            if (!shouldRenderNow) {
                return (
                    <FallbackBox>
                        <FallbackText>이미지 준비중</FallbackText>
                    </FallbackBox>
                );
            }
            return (
                <SliderBox>
                    <Slider
                        key={`${keySuffix}-${resolvedMediaList.length}-${containsGif ? gifResetKey : 'static'}`}
                        {...sliderSettings}
                    >
                        {resolvedMediaList.map((media, idx) => (
                            <Img
                                key={`${media.safe || "img"}-${idx}-${containsGif ? gifResetKey : 'static'}`}
                                src={withGifReset(media.full)}
                                alt="media"
                                $fit={fit}
                                loading="lazy"
                            />
                        ))}
                    </Slider>
                </SliderBox>
                );
        }

        if (isVideo) {
            if (!isActive && !forceLoad) {
                return (
                    <VideoBox>
                        <InactiveVideo>
                            <span>영상 준비중</span>
                        </InactiveVideo>
                    </VideoBox>
                );
            }
            if (isBad) {
                return (
                    <FallbackBox>
                        <FallbackText>미디어를 불러올 수 없습니다.</FallbackText>
                    </FallbackBox>
                );
            }
            return (
                <VideoBox>
                    {/* AutoPlayVideo가 내부에서 muted/playsInline/autoplay 처리한다고 가정 */}
                    <AutoPlayVideo
                        key={`${currentMedia?.safe || "video"}-${keySuffix}`}
                        src={fullUrl}
                        controls={false}
                        isActive={isActive}
                        // loop
                        // onError가 지원된다면 실패 시 다음으로 넘김
                        // @ts-ignore
                        onError={() => {
                            badSrcRef.current.add(fullUrl);
                            advanceToNext();
                        }}
                    />
                </VideoBox>
            );
        }

        if (!shouldRenderNow) {
            return (
                <FallbackBox>
                    <FallbackText>이미지 준비중</FallbackText>
                </FallbackBox>
            );
        }

        // 이미지
        return (
            <Img
                key={containsGif ? `${current}-${gifResetKey}` : undefined}
                src={withGifReset(fullUrl)}
                alt="media"
                $fit={fit}
                loading="lazy"
            />
        );
    };

    // === FULL (1면) ===
    if (type === "full") {
        if (hasSaleType) {
            // 판매정보가 있으면 좌(미디어) / 우(텍스트)
            const titleColor = resolveTransactionColor(saleInfo?.transactionType, saleInfo?.saleType);
            return (
                <FullSplitRoot>
                    <FullLeft>{renderMedia(`full-${num}`)}</FullLeft>

                    <FullRight ref={fullTextRef} $scale={textScale}>
                        <FullTitle $scale={textScale}>
                            {saleInfo?.saleType}
                            {saleInfo?.transactionType && (
                                <FullColorTitle
                                    $scale={textScale}
                                    style={{ backgroundColor: titleColor }}
                                >
                                    {saleInfo?.transactionType}
                                </FullColorTitle>
                            )}
                        </FullTitle>
                        {saleInfo?.price && <FullPrice $scale={textScale}>{saleInfo.price}</FullPrice>}
                        {saleInfo?.address && <FullAddress $scale={textScale}>{saleInfo.address}</FullAddress>}
                        {saleInfo?.size && <FullLabel $scale={textScale}>{saleInfo.size}</FullLabel>}
                        {saleInfo?.desc && <FullDesc $scale={textScale}>{saleInfo.desc}</FullDesc>}
                    </FullRight>
                </FullSplitRoot>
            );
        }

        // 판매정보 없으면 전체 미디어
        return <FillRoot>{renderMedia(`full-${num}`)}</FillRoot>;
    }

    // === TWO (2면) ===
    if (type === "two") {
        return <TwoTopFull>{renderMedia(`two-${num}`)}</TwoTopFull>;
    }

    // === SIX (6면) ===
    return <FillRoot>{renderMedia(`six-${num}`)}</FillRoot>;
};

export const FullImageList = memo(FullImageListComponent);

/* ---------- 스타일 (부모 크기 100% 기준) ---------- */
const FillRoot = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const VideoBox = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    /* AutoPlayVideo 내부가 video 엘리먼트라면 아래가 적용됨 */

    video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: transparent;
    }
`;

const InactiveVideo = styled.div`
    width: 100%;
    height: 100%;
    background: var(--fallback-bg, #ffffff);
    color: var(--fallback-color, #000000);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    letter-spacing: 0.1em;
`;

const Img = styled.img<{ $fit: FitMode }>`
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    transform: translateZ(0);
`;

const SliderBox = styled.div`
    width: 100%;
    height: 100%;

    .slick-slider,
    .slick-list,
    .slick-track {
        height: 100%;
    }

    .slick-slide > div {
        height: 100%;
    }
`;

/* ---- FULL 분할(이미지+텍스트) ---- */
const FullSplitRoot = styled.div`
    width: 100%;
    height: 100%;
    display: flex;

    & > div {
        overflow: hidden;
    }
`;
const FullLeft = styled.div`
    flex: 0 0 52%;
    min-width: 0;
    background-color: var(--media-bg, #fff);
`;
const FullRight = styled.div<{ $scale: number }>`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${({ $scale }) => `${Math.round(16 + 18 * $scale)}px`};
    padding: ${({ $scale }) => `${Math.round(18 + 28 * $scale)}px`};
    background: var(--card-bg, rgba(255, 255, 255, 0.96));
    border: 1px solid var(--card-border, #e6ecf5);
`;

const FullTitle = styled.div<{ $scale: number }>`
    font-weight: 800;
    font-size: ${({ $scale }) => `${(48 * $scale + 10).toFixed(2)}px`};
    display: flex;
    gap: ${({ $scale }) => `${Math.round(8 + 10 * $scale)}px`};
    color: var(--text-main, #0b1b2e);
    text-align: center;
    align-items: center;
`;
const FullColorTitle = styled.span<{ $scale: number }>`
    color: #FFF;
    border-radius: 50px;
    padding: ${({ $scale }) => `${Math.max(8, Math.round(10 * $scale))}px ${Math.max(14, Math.round(18 * $scale))}px`};
    font-size: ${({ $scale }) => `${(24 * $scale + 12).toFixed(2)}px`};
    line-height: 1;
    display: flex;
    align-items: center;
`;
const FullAddress = styled.div<{ $scale: number }>`
    font-weight: 800;
    font-size: ${({ $scale }) => `${(28 * $scale + 12).toFixed(2)}px`};
    color: var(--text-main, #0b1b2e);
    text-align: center;
    white-space: pre-wrap;
`;
const FullPrice = styled.div<{ $scale: number }>`
    font-weight: 900;
    font-size: ${({ $scale }) => `${(34 * $scale + 16).toFixed(2)}px`};
    color: var(--price-color, #000);
    text-align: center;
    background-color: var(--price-bg, #FACC15);
    padding: ${({ $scale }) => `${Math.max(8, Math.round(12 * $scale))}px ${Math.max(14, Math.round(20 * $scale))}px`};
    border-radius: 12px;
    line-height: 1.05;
    max-width: 100%;
    word-break: keep-all;
    white-space: pre-wrap;
    text-shadow: var(--price-shadow, none);
`;
const FullLabel = styled.div<{ $scale: number }>`
    font-weight: 700;
    font-size: ${({ $scale }) => `${(24 * $scale + 10).toFixed(2)}px`};
    color: var(--text-sub, #2c4159);
    white-space: pre-wrap;
    text-align: center;
`;
const FullDesc = styled.div<{ $scale: number }>`
    font-weight: 600;
    font-size: ${({ $scale }) => `${(22 * $scale + 8).toFixed(2)}px`};
    color: var(--text-sub, #2c4159);
    white-space: pre-wrap;
    text-align: center;
`;

/* ---- TWO 상단: 전폭 이미지 ---- */
const TwoTopFull = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 12px;
    overflow: hidden;
`;

/* ---- 공통 폴백 ---- */
const FallbackBox = styled.div`
    width: 100%;
    height: 100%;
    background: var(--fallback-bg, #f5f8fe);
    border: 1px solid var(--fallback-border, #e6ecf5);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FallbackText = styled(Text)`
    color: var(--fallback-color, #2c4159);
    font-weight: 700;
`;
